<script setup lang="ts">
import { ref, reactive, watch, onUnmounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useAdminStore, type BillingUnit, type Product } from '../stores/admin';
import ImageCropperModal from './ImageCropperModal.vue';

const store = useAdminStore();

const drawerVisible = ref(false);
const isEditing = ref(false);

const cropperVisible = ref(false);
const rawCropFile = ref<File | null>(null);
const imagePreview = ref('');
let previewObjectUrl = '';

const priceDrafts = reactive<Record<string, string>>({});

function initPriceDraft(product: Product) {
  if (priceDrafts[product.id] === undefined) priceDrafts[product.id] = product.priceCents != null ? (product.priceCents / 100).toFixed(2) : '';
}

async function savePrice(product: Product) {
  try {
    const raw = (priceDrafts[product.id] ?? '').trim();
    if (!raw) return ElMessage.warning('商品价格必须填写');
    const cents = store.yuanToCents(raw);
    if (cents == null || cents <= 0) return ElMessage.warning('价格请输入大于 0 的数字，最多两位小数');
    if (cents === product.priceCents) return;
    await store.updateProductPrice(product.id, cents);
    product.priceCents = cents;
    ElMessage.success('价格已保存');
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '价格保存失败');
  }
}

function priceUnitLabel(product: Product) {
  return product.billingUnit === 'JIN' || product.saleMode === 'QUOTE' ? '元/斤' : '元/件';
}

function priceUnchanged(product: Product) {
  const raw = (priceDrafts[product.id] ?? '').trim();
  return store.yuanToCents(raw) === product.priceCents;
}

const form = reactive({
  id: '',
  title: '',
  categoryId: '',
  billingUnit: 'PIECE' as BillingUnit,
  priceYuan: '',
  stock: '',
  active: true,
  supportPickup: true,
  supportExpress: true,
  image: null as File | null
});

function setImagePreview(source: File | string | null | undefined) {
  if (previewObjectUrl) {
    URL.revokeObjectURL(previewObjectUrl);
    previewObjectUrl = '';
  }
  if (source instanceof File) {
    previewObjectUrl = URL.createObjectURL(source);
    imagePreview.value = previewObjectUrl;
  } else {
    imagePreview.value = typeof source === 'string' ? source : '';
  }
}

watch(() => store.products, (products) => {
  for (const product of products) initPriceDraft(product);
}, { immediate: true });

function openAddProduct() {
  isEditing.value = false;
  Object.assign(form, {
    id: `p-${Date.now().toString().slice(-6)}`,
    title: '',
    categoryId: '',
    billingUnit: 'PIECE',
    priceYuan: '',
    stock: '100',
    active: true,
    supportPickup: true,
    supportExpress: true,
    image: null
  });
  setImagePreview('');
  drawerVisible.value = true;
}

function openEditProduct(product: Product) {
  isEditing.value = true;
  const hasPickup = product.fulfillmentTypes?.includes('PICKUP') ?? true;
  const hasExpress = product.fulfillmentTypes?.includes('EXPRESS') ?? true;
  
  Object.assign(form, {
    id: product.id,
    title: product.title,
    categoryId: product.categoryId ?? '',
    billingUnit: product.billingUnit ?? (product.saleMode === 'QUOTE' ? 'JIN' : 'PIECE'),
    priceYuan: product.priceCents != null ? (product.priceCents / 100).toFixed(2) : '',
    stock: String(product.stock),
    active: product.active,
    supportPickup: hasPickup,
    supportExpress: hasExpress,
    image: null
  });
  setImagePreview(store.productImageUrl(product));
  drawerVisible.value = true;
}

function onImageSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (file) {
    rawCropFile.value = file;
    cropperVisible.value = true;
  }
}

function onProductCropped(croppedFile: File) {
  form.image = croppedFile;
  setImagePreview(croppedFile);
  ElMessage.success("商品图片已裁切处理，保存后生效");
}

onUnmounted(() => setImagePreview(''));

async function handleDeleteProduct(product: Product) {
  try {
    await ElMessageBox.confirm(`确定要删除商品 "${product.title}" 吗？删除后将从后台商品列表和小程序隐藏，历史订单仍会保留。`, "删除商品", {
      confirmButtonText: "确认删除",
      cancelButtonText: "取消",
      type: "warning"
    });
    await store.deleteProduct(product.id);
    ElMessage.success("商品已成功删除");
  } catch (error) {
    if (error === 'cancel' || error === 'close') return;
    ElMessage.error(error instanceof Error ? error.message : '删除商品失败');
  }
}

async function handleToggleStatus(product: Product, active: boolean) {
  try {
    await store.toggleProductStatus(product.id, active);
    ElMessage.success(active ? "商品已上架售卖" : "商品已暂不上架");
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "操作失败");
  }
}

async function handleSaveProduct() {
  if (!store.apiReady) return ElMessage.warning("请先配置后台 API 域名");
  if (!store.adminSessionToken.trim()) return ElMessage.warning("请先登录后台");
  if (!form.id.trim() || !form.title.trim()) return ElMessage.warning("商品编号和名称必填");
  if (!form.priceYuan.trim()) return ElMessage.warning("商品必须填写价格（元）");
  if (!isEditing.value && !form.image && !imagePreview.value) return ElMessage.warning("请上传并裁切商品主图");
  
  const priceCents = form.priceYuan.trim() ? store.yuanToCents(form.priceYuan) : null;
  if (priceCents == null || priceCents <= 0) return ElMessage.warning("价格请输入大于 0 的数字，最多两位小数");
  const stock = form.billingUnit === 'JIN' ? 0 : Number(form.stock || 0);
  if (form.billingUnit === 'PIECE' && (!Number.isInteger(stock) || stock < 0)) return ElMessage.warning("按件库存请输入非负整数");

  store.publishLoading = true;
  try {
    const body = new FormData();
    body.append("id", form.id.trim());
    body.append("title", form.title.trim());
    body.append("categoryId", form.categoryId.trim());
    body.append("billingUnit", form.billingUnit);
    body.append("saleMode", 'FIXED');
    body.append("priceCents", priceCents == null ? "" : String(priceCents));
    body.append("stock", String(stock));
    body.append("active", String(form.active));
    body.append("fulfillmentTypes", JSON.stringify([...(form.supportPickup ? ["PICKUP"] : []), ...(form.supportExpress ? ["EXPRESS"] : [])]));
    if (form.image) body.append("image", form.image);

    await store.adminFetch("/api/admin/products", { method: "POST", body });
    ElMessage.success(isEditing.value ? "商品修改已保存" : "新商品已上架");
    drawerVisible.value = false;
    await store.loadProducts();
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : "保存失败");
  } finally {
    store.publishLoading = false;
  }
}
</script>

<template>
  <div class="product-management">
    <div class="panel">
      <div class="panel-header-row">
        <div>
          <p class="eyebrow">海货目录</p>
          <h2>商品管理 (共 {{ store.products.length }} 件)</h2>
        </div>
        <el-button type="primary" size="large" @click="openAddProduct">
          + 上架新商品
        </el-button>
      </div>

      <!-- Desktop Table View -->
      <div class="desktop-only">
        <el-table :data="store.products" v-loading="store.loading" empty-text="暂无商品，点击右上角开始上架">
          <el-table-column label="商品图片与标题" min-width="240">
            <template #default="scope">
              <div class="product-cell">
                <img v-if="store.productImageUrl(scope.row)" :src="store.productImageUrl(scope.row)" :alt="scope.row.title" class="product-thumb" />
                <div v-else class="product-thumb placeholder">海</div>
                <div>
                  <div class="product-name">{{ scope.row.title }}</div>
                  <div class="product-id">编号: {{ scope.row.id }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="计费单位" width="120">
            <template #default="scope">
              <el-tag :type="scope.row.saleMode === 'FIXED' ? 'success' : 'warning'" effect="light">
                {{ scope.row.billingUnit === 'JIN' || scope.row.saleMode === 'QUOTE' ? '按斤' : '按件' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="价格（可直接修改）" min-width="190">
            <template #default="scope">
              <div class="inline-price-editor">
                <el-input
                  v-model="priceDrafts[scope.row.id]"
                  size="small"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="请输入价格"
                  @keyup.enter="savePrice(scope.row)"
                >
                  <template #append>{{ priceUnitLabel(scope.row) }}</template>
                </el-input>
                <el-button type="primary" size="small" :disabled="priceUnchanged(scope.row)" @click="savePrice(scope.row)">保存</el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="可售库存" width="100"><template #default="scope">{{ scope.row.billingUnit === 'JIN' ? '按在售状态' : scope.row.stock }}</template></el-table-column>
          <el-table-column label="上下架状态" width="140">
            <template #default="scope">
              <el-switch
                :model-value="scope.row.active"
                active-text="上架售卖"
                inactive-text="暂不上架"
                inline-prompt
                @change="(val: boolean) => handleToggleStatus(scope.row, val)"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180" align="right">
            <template #default="scope">
              <el-button type="primary" size="small" text @click="openEditProduct(scope.row)">编辑</el-button>
              <el-button type="danger" size="small" text @click="handleDeleteProduct(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Mobile Card List View -->
      <div class="mobile-only mobile-card-list">
        <div v-if="store.products.length === 0" class="empty-mobile">暂无商品，点击右上角开始上架</div>
        <div v-for="item in store.products" :key="item.id" class="product-card">
          <img v-if="store.productImageUrl(item)" :src="store.productImageUrl(item)" :alt="item.title" class="mobile-thumb" />
          <div v-else class="mobile-thumb placeholder">海</div>
          
          <div class="mobile-card-content">
            <div class="mobile-card-header">
              <span class="mobile-title">{{ item.title }}</span>
              <el-switch
                :model-value="item.active"
                active-text="上架售卖"
                inactive-text="暂不上架"
                inline-prompt
                size="small"
                @change="(val: boolean) => handleToggleStatus(item, val)"
              />
            </div>
            
            <div class="mobile-meta-row">
              <div class="inline-price-editor mobile-price-editor">
                <el-input
                  v-model="priceDrafts[item.id]"
                  size="small"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="请输入价格"
                  @keyup.enter="savePrice(item)"
                >
                  <template #append>{{ priceUnitLabel(item) }}</template>
                </el-input>
                <el-button type="primary" size="small" :disabled="priceUnchanged(item)" @click="savePrice(item)">保存</el-button>
              </div>
              <span class="stock-text">{{ item.billingUnit === 'JIN' ? '按在售状态' : `库存: ${item.stock}` }}</span>
            </div>
            
            <div class="mobile-card-actions">
              <el-button type="primary" size="small" plain @click="openEditProduct(item)">编辑</el-button>
              <el-button type="danger" size="small" plain @click="handleDeleteProduct(item)">删除</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Drawer / Modal Form -->
    <el-drawer v-model="drawerVisible" :title="isEditing ? '编辑商品' : '上架新商品'" size="90%" max-width="540px" destroy-on-close>
      <el-form label-position="top" class="product-form">
        <el-form-item label="商品编号 (唯一标识)" required>
          <el-input v-model="form.id" :disabled="isEditing" placeholder="例: crab-001" />
        </el-form-item>
        
        <el-form-item label="商品名称" required>
          <el-input v-model="form.title" placeholder="例: 现捞活鲜梭子蟹" />
        </el-form-item>

        <el-form-item label="商品分类">
          <el-select v-model="form.categoryId" placeholder="选择商品分类" style="width: 100%">
            <el-option label="未分类" value="" />
            <el-option v-for="cat in store.categories" :key="cat.id" :label="cat.name" :value="cat.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="计费单位">
          <el-radio-group v-model="form.billingUnit">
            <el-radio-button value="PIECE">按件（元/件）</el-radio-button>
            <el-radio-button value="JIN">按斤（元/斤）</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="价格（元/件或元/斤）" required>
          <el-input v-model="form.priceYuan" type="number" step="0.01" placeholder="例如 68.88" />
        </el-form-item>

        <el-form-item v-if="form.billingUnit === 'PIECE'" label="可售库存" required>
          <el-input v-model="form.stock" type="number" placeholder="库存数量" />
        </el-form-item>

        <el-form-item label="支持的配送/履约方式">
          <el-checkbox v-model="form.supportPickup">到店自提</el-checkbox>
          <el-checkbox v-model="form.supportExpress">快递发货</el-checkbox>
        </el-form-item>

        <el-form-item label="商品图片">
          <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" @change="onImageSelected" class="file-input" />
          <div class="hint-text">支持 JPG/PNG/WebP，单张不大于 5MB</div>
          <div v-if="imagePreview" class="selected-image-preview">
            <img :src="imagePreview" alt="商品图片预览" />
            <span>{{ form.image ? '新图片预览（保存后生效）' : '当前商品图片' }}</span>
          </div>
        </el-form-item>

        <el-form-item label="上架状态">
          <el-switch v-model="form.active" active-text="直接在小程序中售卖" inactive-text="暂不上架售卖" />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="drawer-footer">
          <el-button @click="drawerVisible = false">取消</el-button>
          <el-button type="primary" :loading="store.publishLoading" @click="handleSaveProduct">
            {{ isEditing ? '保存修改' : '确认上架' }}
          </el-button>
        </div>
      </template>
    </el-drawer>

    <!-- Image Cropper Modal -->
    <ImageCropperModal
      v-model:visible="cropperVisible"
      :image-file="rawCropFile"
      :aspect-ratio="1"
      title="商品主图裁切控制"
      @crop="onProductCropped"
    />
  </div>
</template>

<style scoped>
.panel-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.product-thumb {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  background: #f0f4f7;
  flex-shrink: 0;
}
.product-thumb.placeholder {
  display: grid;
  place-items: center;
  color: #909399;
  font-weight: bold;
}
.product-name {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}
.product-id {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}
.price-text {
  font-size: 15px;
  font-weight: bold;
  color: #f56c6c;
}
.inline-price-editor {
  display: flex;
  align-items: center;
  gap: 8px;
}
.selected-image-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  color: #606266;
  font-size: 12px;
}
.selected-image-preview img {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #f5f7fa;
}
.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Mobile Styles */
.desktop-only { display: block; }
.mobile-only { display: none; }

@media (max-width: 768px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }

  .panel-header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .panel-header-row .el-button {
    width: 100%;
  }

  .mobile-card-list {
    display: grid;
    gap: 12px;
  }
  .product-card {
    display: flex;
    gap: 12px;
    padding: 12px;
    background: #fff;
    border: 1px solid #ebedf0;
    border-radius: 8px;
  }
  .mobile-thumb {
    width: 70px;
    height: 70px;
    border-radius: 6px;
    object-fit: cover;
    background: #f0f4f7;
    flex-shrink: 0;
  }
  .mobile-thumb.placeholder {
    display: grid;
    place-items: center;
    color: #909399;
  }
  .mobile-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .mobile-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .mobile-title {
    font-weight: 600;
    font-size: 14px;
    color: #303133;
  }
  .mobile-meta-row {
    display: flex;
    justify-content: space-between;
    margin: 6px 0;
    font-size: 13px;
  }
  .stock-text {
    color: #606266;
  }
  .mobile-card-actions {
    display: flex;
    justify-content: flex-end;
  }
.empty-mobile {
    text-align: center;
    padding: 30px;
    color: #909399;
  }
  .mobile-price-editor {
    flex: 1;
  }
}
</style>
